/**
 * @description
 * create an immutable copy (deep clone) of any object, the returned object an all its
 * descendants will have new instances when cloning is complete.
 * this behavior can be altered
 * @param obj target object to clone
 * @returns a new instance of original object
 */
export function deepCloneImmutable<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCloneImmutable(item)) as T;
  }

  const clonedObject: Partial<T> = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      clonedObject[key] = deepCloneImmutable(obj[key]);
    }
  }

  return clonedObject as T;
}

export type TCloneOptions = {
  ignoreMutationNulls?: boolean;
};

export type TDeepPartial<T> = {
  [K in keyof T]?: TDeepPartial<T[K]>;
};

/**
 * @description
 * deep clone and extend any object type with a partial of same type, the returned object an
 * all its descendants will have new instances when cloning is complete.
 * @param source
 * @param mutation
 * @param options
 * @returns
 */
export function deepCloneExtendImmutable<T>(source: T, mutation: TDeepPartial<T>, options?: TCloneOptions): T {
  function clone(sourceObj: any, mutationObj: any): any {
    if (sourceObj === null || typeof sourceObj !== 'object') {
      return sourceObj;
    }

    if (Array.isArray(sourceObj)) {
      return sourceObj.map((item, index) => clone(item, mutationObj[index]));
    }

    const clonedObject: any = { ...sourceObj };

    for (const key in mutationObj) {
      if (mutationObj.hasOwnProperty(key)) {
        const mutationValue = mutationObj[key];

        if (options?.ignoreMutationNulls && mutationValue === null) {
          /** do not copy nulls from mutation into target */
          continue;
        }

        clonedObject[key] = clone(sourceObj[key], mutationValue);
      }
    }

    return clonedObject;
  }

  return clone(source, mutation);
}

/**
 * @description
 * checks that an object has truthy value for each key (the fn sees the 0 value as truthy value).
 * all nested objects will be also checked to verify that their keys hold truthy values.
 * an empty object (w/o keys at all) will return false
 */
export function verifyObjectValues<T>(obj: T): boolean {
  function deepCheckTruthy(innerObj: any): boolean {
    if (innerObj === null || innerObj === undefined || Object.keys(obj).length === 0) {
      return false;
    }

    if (typeof innerObj === 'object') {
      return Object.values(innerObj).every((value) => deepCheckTruthy(value));
    }

    return innerObj !== undefined && innerObj !== null && (innerObj !== '' || innerObj === 0);
  }

  return deepCheckTruthy(obj);
}

/**
 * @description
 * returns new object without empty values
 * for flat and not nested
 */
export function excludeEmptyValues<T>(obj: T): T {
  const newObj: Partial<T> = {};

  // Object.entries(obj)
  // .filter(([, value]) => value !== null && value !== undefined && value !== '')
  //   .forEach(([key, value]) => (newObj[key] = value));

  for (const prop in obj) {
    if (obj[prop] || obj[prop] === 0) {
      newObj[prop] = obj[prop];
    }
  }

  return newObj as T;
}
export function extractValueByKey<T>(obj: any, key: string): T {
  if (obj?.hasOwnProperty(key)) {
    return obj[key] as T;
  }
  return undefined;
}

export function zeroToUndefined<T>(value: T, prop: keyof T): T {
  if (value[prop] === 0) {
    return { ...value, [prop]: undefined };
  }
  return value;
}
